<template>
  <div class="email-editor">
    <h4>{{ titleText }}</h4>
    <p>{{ descText }}</p>
    <b-form>
      <!-- Select language for the template -->
      <b-form-group :label="getLabel('LANGUAGE')" label-class="mr-2">
        <b-input-group class="mb-3">
          <b-form-select
            v-model="selectedLanguage"
            class="form-control"
            @input="selectTemplate()"
          >
            <b-form-select-option
              v-for="(language, index) in languages"
              :key="index"
              :value="language.code"
            >
              {{ `${language.local_name} (${language.english_name})` }}
            </b-form-select-option>
          </b-form-select>
        </b-input-group>
      </b-form-group>

      <!-- Email subject and body -->
      <b-form-group
        :label="
          `${getLabel('EMAIL_SUBJECT')}* (${
            emailSubject.length
          }/${subjectMaxLength})`
        "
      >
        <b-form-input
          v-model="emailSubject"
          :state="validSubject"
          type="text"
          required
        />
      </b-form-group>
      <b-form-group
        :label="
          `${getLabel('EMAIL_BODY')}* (${emailBody.length}/${bodyMaxLength})`
        "
      >
        <b-form-textarea
          v-model="emailBody"
          :state="validBody"
          required
          rows="3"
          max-rows="20"
        />
      </b-form-group>

      <!-- Warnings for any errors -->
      <b-alert variant="danger" :show="warningMessages.length > 0">
        <div v-for="(message, index) in warningMessages" :key="index">
          {{ message }}
        </div>
      </b-alert>

      <!-- Save button -->
      <b-button variant="primary" :disabled="!canSave" @click="save"
        ><span>{{ getLabel('SAVE') }}</span></b-button
      >
      <!-- Reset button -->
      <b-button
        v-if="hasChanges"
        @click="reset"
        variant="outline-secondary"
        class="email-template__reset-button"
        ><span>Reset default</span></b-button
      >
    </b-form>
    <br />
    <!-- <b-form @submit.stop.prevent="submit()"> -->
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';

const $emails = require('@/assets/emails.json');

const SUBJECT_MAX_LENGTH = 100;
const BODY_MAX_LENGTH = 500;

export default {
  props: {
    templates: {
      type: Array,
      default: null,
    },
    type: {
      type: String,
      default: '',
      required: true,
      validator(val) {
        return ['welcome', 'reset_password'].indexOf(val) > -1;
      },
    },
  },
  components: {},
  data() {
    return {
      loading: true,
      selectedLanguage: 'en',
      emailSubject: '',
      emailBody: '',
      requiredVariables: [],
      subjectMaxLength: SUBJECT_MAX_LENGTH,
      bodyMaxLength: BODY_MAX_LENGTH,
    };
  },
  computed: {
    ...mapState('languages', {
      languages: 'languages',
    }),
    ...mapGetters('translations', {
      getLabel: 'getLabel',
    }),
    titleText() {
      if (this.defaultTemplate) return this.defaultTemplate.title;
      return '';
    },
    descText() {
      if (this.defaultTemplate) return this.defaultTemplate.description;
      return '';
    },
    defaultTemplate() {
      if (this.type) return $emails[this.type].en;
      return {};
    },
    missingVariables() {
      const missingVariables = [];
      if (!this.requiredVariables) return missingVariables;
      this.requiredVariables.forEach((variable) => {
        if (this.emailBody.indexOf(variable) < 0) {
          missingVariables.push(variable);
        }
      });
      return missingVariables;
    },
    warningMessages() {
      const warnings = [];
      if (!this.emailSubject.length) warnings.push('Email subject required');
      if (this.missingVariables.length) {
        // TODO translate?
        warnings.push(
          `Missing required variables: ${this.missingVariables.join(', ')}`,
        );
      }
      return warnings;
    },
    canSave() {
      if (this.warningMessages.length !== 0) return false;
      if (!this.validSubject) return false;
      if (!this.validBody) return false;
      return true;
    },
    validSubject() {
      return (
        this.emailSubject.length &&
        this.emailSubject.length <= this.subjectMaxLength
      );
    },
    validBody() {
      return (
        !this.missingVariables.length &&
        this.emailBody.length <= this.bodyMaxLength
      );
    },
    hasChanges() {
      if (this.defaultTemplate.email_subject !== this.emailSubject) return true;
      if (this.defaultTemplate.email_body !== this.emailBody) return true;
      return false;
    },
  },
  methods: {
    selectTemplate() {
      if (!this.templates) return;
      const lang = this.selectedLanguage;
      const filteredTemplates = this.templates
        .filter((template) => template.language === lang)
        .filter((template) => template.type === this.type);
      const filteredTemplate = filteredTemplates[0];
      const finalTemplate = filteredTemplate || this.defaultTemplate;
      this.emailSubject = finalTemplate.email_subject;
      this.emailBody = finalTemplate.email_body;
      this.requiredVariables = finalTemplate.variables || [];
    },
    save() {
      const template = {
        email_subject: this.emailSubject,
        email_body: this.emailBody,
        language: this.selectedLanguage,
        type: this.type,
      };
      this.$emit('save', template);
    },
    async reset() {
      // eslint-disable-next-line no-restricted-globals
      const confirmReset = confirm('Reset the email template to default?');
      if (!confirmReset) return;
      this.emailSubject = this.defaultTemplate.email_subject;
      this.emailBody = this.defaultTemplate.email_body;
    },
  },
  watch: {
    templates: {
      immediate: true,
      handler() {
        this.selectTemplate();
      },
    },
  },
};
</script>
<style scoped>
.email-template__reset-button {
  margin-left: 20px;
}
</style>
